import type { GetServerSidePropsContext } from "next";
import { signIn } from "next-auth/react";
import { Trans } from "next-i18next";
import { useRouter } from "next/router";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";

import { WEBAPP_URL, WEBSITE_URL } from "@calcom/lib/constants";
import { useLocale } from "@calcom/lib/hooks/useLocale";
import { collectPageParameters, telemetryEventTypes } from "@calcom/lib/telemetry";
import { useTelemetry } from "@calcom/lib/telemetry-client";
import prisma from "@calcom/prisma";
import { inferSSRProps } from "@calcom/types/inferSSRProps";
import { Alert, Form, Button, EmailField, PasswordField } from "@calcom/ui";

import { GettingStartedLayout } from "@components/getting-started/layout";
import CheckboxField from "@components/ui/form/CheckboxField";

import { asStringOrNull } from "../../lib/asStringOrNull";
// import signupMock from "../../public/signup-mock.png";
import { IS_GOOGLE_LOGIN_ENABLED } from "../../server/lib/constants";
import { ssrInit } from "../../server/lib/ssr";

export default function SignupPage() {
  const { t } = useLocale();
  return (
    <GettingStartedLayout>
      <h2 className="font-cal mb-4 text-2xl font-bold [text-wrap-style:balance] md:text-3xl">
        Experte bei LuckyBrunch werden
      </h2>
      <p className="mb-8 text-gray-600">
        Präsentiere deine Expertise und knüpfe Kontakte zu Kunden, die deine Beratung benötigen.
      </p>

      <SignupForm />

      <div className="mt-8 text-center text-sm text-gray-600">
        <a href={`${WEBSITE_URL}/auth/login`} className="text-brand-500 font-medium">
          {t("have_an_account")}
        </a>
      </div>
    </GettingStartedLayout>
  );
}

type FormValues = {
  email: string;
  password: string;
  passwordcheck: string;
  apiError: string;
  acceptTerms: boolean;
};

function SignupForm({ prepopulateFormValues }: inferSSRProps<typeof getServerSideProps>) {
  const { t } = useLocale();
  const router = useRouter();
  const telemetry = useTelemetry();

  const methods = useForm<FormValues>({
    defaultValues: prepopulateFormValues,
  });
  const {
    register,
    formState: { errors, isSubmitting },
  } = methods;

  const handleErrors = async (resp: Response) => {
    if (!resp.ok) {
      const err = await resp.json();
      throw new Error(err.message);
    }
  };

  const signUp: SubmitHandler<FormValues> = async (data) => {
    methods.clearErrors();
    await fetch("/api/auth/signup", {
      body: JSON.stringify({
        ...data,
      }),
      headers: {
        "Content-Type": "application/json",
      },
      method: "POST",
    })
      .then(handleErrors)
      .then(async () => {
        telemetry.event(telemetryEventTypes.signup, collectPageParameters());
        try {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (window as any).dataLayer.push({ event: "sign_up_coach" });
        } catch (e) {
          console.error("dataLayer.push error", e);
        }
        await signIn<"credentials">("credentials", {
          ...data,
          callbackUrl: router.query.callbackUrl
            ? `${WEBAPP_URL}/${router.query.callbackUrl}`
            : `${WEBAPP_URL}/getting-started`,
        });
      })
      .catch((err) => {
        methods.setError("apiError", { message: err.message });
      });
  };

  return (
    <FormProvider {...methods}>
      <Form form={methods} handleSubmit={signUp} className="space-y-5">
        {errors.apiError && <Alert severity="error" message={errors.apiError?.message} />}

        <EmailField
          {...register("email")}
          disabled={prepopulateFormValues?.email}
          className="disabled:bg-gray-200 disabled:hover:cursor-not-allowed"
        />
        <PasswordField
          labelProps={{
            className: "block text-sm font-medium text-gray-700",
          }}
          hint={t("lb_password_hint")}
          {...register("password")}
          className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-black focus:outline-none focus:ring-black sm:text-sm"
        />
        <PasswordField
          label={t("confirm_password")}
          {...register("passwordcheck", {
            validate: (value) =>
              value === methods.watch("password") || (t("error_password_mismatch") as string),
          })}
        />

        <div>
          <p className="pb-3 text-sm">
            <Trans i18nKey="lb_terms">
              Please read our
              <a
                href="https://luckybrunch.de/allgemeine-geschaftsbedingungen"
                target="_blank"
                rel="noreferrer"
                className="text-brand-800 underline">
                General Terms and Conditions
              </a>
              carefully and accept them in order to proceed.
            </Trans>
          </p>
          <CheckboxField description={t("lb_accept_terms")} required {...register("acceptTerms")} />
        </div>

        <div className="flex pt-3">
          <Button type="submit" loading={isSubmitting} className="w-full justify-center">
            {t("create_account")}
          </Button>
        </div>
      </Form>
    </FormProvider>
  );
}

export const getServerSideProps = async (ctx: GetServerSidePropsContext) => {
  const ssr = await ssrInit(ctx);
  const token = asStringOrNull(ctx.query.token);

  const props = {
    trpcState: ssr.dehydrate(),
    isGoogleLoginEnabled: IS_GOOGLE_LOGIN_ENABLED,
    isSAMLLoginEnabled: false,
    prepopulateFormValues: undefined,
  };

  if (process.env.NEXT_PUBLIC_DISABLE_SIGNUP === "true") {
    return {
      notFound: true,
    };
  }

  // no token given, treat as a normal signup without verification token
  if (!token) {
    return {
      props: JSON.parse(JSON.stringify(props)),
    };
  }

  const verificationToken = await prisma.verificationToken.findUnique({
    where: {
      token,
    },
  });

  if (!verificationToken || verificationToken.expires < new Date()) {
    return {
      notFound: true,
    };
  }

  const existingUser = await prisma.user.findFirst({
    where: {
      AND: [
        {
          email: verificationToken?.identifier,
        },
        {
          emailVerified: {
            not: null,
          },
        },
      ],
    },
  });

  if (existingUser) {
    return {
      redirect: {
        permanent: false,
        destination: "/auth/login?callbackUrl=" + `${WEBAPP_URL}/${ctx.query.callbackUrl}`,
      },
    };
  }

  return {
    props: {
      ...props,
      prepopulateFormValues: {
        email: verificationToken.identifier,
      },
    },
  };
};
